































































































































import LazyComponent from "v-lazy-component/vue2";
import {
  ref,
  computed,
  useContext,
  useRoute,
  useRouter,
  defineComponent,
  PropType,
  onMounted,
  onBeforeUnmount,
  watch,
  useFetch,
} from "@nuxtjs/composition-api";

import {
  getSwatchData as getProductSwatchData,
  getName as getProductName,
} from "~/modules/catalog/product/getters/productGetters";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";

import {
  getTotalReviews,
  getAverageRating,
} from "~/modules/review/getters/reviewGetters";

import { useCart } from "~/modules/checkout/composables/useCart";
import { useUser } from "~/modules/customer/composables/useUser";
import { Product } from "~/modules/catalog/product/types";
import useProductStock from "~/modules/catalog/product/composables/useProductStock";

import { usePageStore } from "~/stores/page";
import { copyCode, nl2br } from "~/helpers";
import { getConfigurableOptionsByProduct } from "~/modules/catalog/product/helpers";
import ProductSizeGuide from "./productDetail/ProductSizeGuide.vue";
import ProductDescription from "./productDetail/ProductDescription.vue";
import ProductInfo from "./productDetail/ProductInfo.vue";
import ProductAvaiableOptions from "./productDetail/ProductAvaiableOptions.vue";
import ProductGallery from "./productDetail/ProductGallery.vue";
import type { SelectedOption } from "~/modules/catalog/types";
import CheckStoreStockButton from "~/modules/catalog/product/components/productDetail/CheckStoreStockButton.vue";
// import useWishlist from "~/modules/wishlist/composables/useWishlist";
import { useUiState } from "~/composables";
import ProductService from "~/modules/catalog/product/components/ProductService.vue";

import { useUiNotification } from "~/composables";
import { useClevertapStore } from "~/stores/clevertap";
import { useEcommercePixel } from "~/stores/ecommercePixel"; // Add this import
import { decodeUid } from "~/helpers";
import { getRegularPrice } from "~/modules/catalog/pricing/getRegularPriceCommand";
import { getFinalPrice } from "~/modules/catalog/pricing/getFinalPriceCommand";

export default defineComponent({
  name: "ProductDetail",
  components: {
    LazyComponent,
    ProductSizeGuide,
    ProductDescription,
    ProductInfo,
    ProductAvaiableOptions,
    ProductGallery,
    SkeletonLoader,
    CheckStoreStockButton,
    ProductService,
  },
  transition: "fade",
  props: {
    product: {
      type: [Object, null] as PropType<Product>,
      default: null,
    },
  },
  setup(props, { emit }) {
    const qty = ref(1);
    // const { addOrRemoveItem, isInWishlist } = useWishlist();
    const { toggleLoginModal } = useUiState();
    const listProductViewed = ref<string[]>([]);
    // const wishlistState = computed(() =>
    //   isInWishlist({ product: props.product })
    // );
    const { getProductListStock, loading: loadingStock } = useProductStock();
    const route = useRoute();
    const router = useRouter();
    const { routeData } = usePageStore();
    const {
      addItem,
      error: cartError,
      loading: isCartLoading,
      canAddToCart,
    } = useCart();
    const { isAuthenticated, user } = useUser(); // Make sure to extract user data
    const { app } = useContext();
    const productShortDescription = computed(
      () => props.product?.short_description?.html || ""
    );
    const attributeUidColor = "color";
    const attributeUidSize = "size";
    const productConfiguration = ref(route.value.query);
    const totalReviews = computed(() => getTotalReviews(props.product));
    const averageRating = computed(() => getAverageRating(props.product));
    const addToCartError = computed(() => cartError.value?.addItem?.message);
    const configurableOptions = computed(() => {
      const configurableOpt = props.product?.configurable_options_map || [];
      if (props.product && props.product.first_child_product) {
        const skuColor =
          props.product.first_child_product?.split("-")?.[1] || "";
        const skuSize =
          props.product.first_child_product?.split("-")?.[2] || "";
        return configurableOpt.map((option) => {
          if (option.attribute_code === "color" && skuColor) {
            const optionCopy = { ...option };
            optionCopy.values = [...option.values].sort((a, b) => {
              if (a.label === skuColor) return -1;
              if (b.label === skuColor) return 1;
              return 0;
            });
            return optionCopy;
          }
          if (option.attribute_code === "size" && skuSize) {
            const optionCopy = { ...option };
            optionCopy.values = [...option.values].sort((a, b) => {
              if (a.label === skuSize) return -1;
              if (b.label === skuSize) return 1;
              return 0;
            });
            return optionCopy;
          }
          return option;
        });
      }
      return configurableOpt;
    });
    // const handleWishlistClick = () => {
    //   if (!isAuthenticated.value) {
    //     toggleLoginModal();
    //     return;
    //   }
    //   addOrRemoveItem({ product: props.product });
    // };
    const updateProductConfiguration = (label: string, value: string) => {
      if (productConfiguration.value[label] === value) return;
      productConfiguration.value[label] = value;
      updateConfiguration();
    };
    const updateConfiguration = () => {
      const routerQuery = { ...productConfiguration.value };
      if (routerQuery.color) {
        const colorOption = configurableOptions.value?.find(
          (option) => option.attribute_code === "color"
        );
        if (colorOption) {
          const colorValue = colorOption.values?.find(
            (val) => val.uid === routerQuery.color
          );
          if (colorValue) {
            routerQuery.color = colorValue.label;
          }
        }
      }
      if (routerQuery.size) {
        const sizeOption = configurableOptions.value?.find(
          (option) => option.attribute_code === "size"
        );
        if (sizeOption) {
          const sizeValue = sizeOption.values?.find(
            (val) => val.uid === routerQuery.size
          );
          if (sizeValue) {
            routerQuery.size = sizeValue.label;
          }
        }
      }
      if (typeof window !== "undefined") {
        const routerData = router.resolve({
          path: `${app.localePath(window.location.pathname)}`,
          query: {
            ...routerQuery,
          },
        });
        window.history.replaceState({}, null, routerData.href);
      }
    };
    const showColorAlert = ref(false);
    const showSizeAlert = ref(false);
    const handleAddToCart = () => {
      if (
        availableOptions.value.size?.length > 0 &&
        !selectedOption.value.size
      ) {
        const optionsElement = document.querySelector(
          ".product__size-guide--mb"
        );
        if (optionsElement) {
          optionsElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        return;
      }

      const sku = findMatchingSku(selectedOption.value);
      const productConfigSku = { ...props.product, sku: sku };
      // Add item to cart
      addItem({
        product: productConfigSku,
        quantity: 1,
      })
        .then(() => {
          // Track the add to cart event after successful addition
          trackAddToCart(productConfigSku, sku);
        })
        .catch((error) => {
          console.error("Error adding item to cart:", error);
        });
    };
    // Function to track add to cart events
    const trackAddToCart = (product, detailSku) => {
      try {
        // Get from CleverTap store to check if the product was viewed from a recommendation
        const fromToData = clevertapStore.getFromtTo;
        const infoFromSearch = clevertapStore.getInforFromSearch;

        // Find stock data for the selected variation - fix for array structure
        const stockData = Array.isArray(dataCheckOptionInstock.value)
          ? dataCheckOptionInstock.value?.find((item) => item.sku === detailSku)
          : dataCheckOptionInstock.value[detailSku];

        // Extract price information from product
        let regularPrice = product.regular_price || 0;
        let finalPrice = product.final_price || 0;

        // Get domain from current route
        const origin =
          typeof window !== "undefined" ? window.location.origin : "";

        // Extract last category for default list info
        const categoriesArray = product.categories || [];
        const lastCategory =
          categoriesArray.length > 0
            ? categoriesArray[categoriesArray.length - 1]
            : null;

        // Build tracking data object with all required parameters
        const trackingData = {
          // Basic product information
          product_id: parentSku.value,
          item_id: detailSku,
          item_name: product.name,

          // Price information
          product_currency: "VND",
          item_price: regularPrice,
          item_discount_price: finalPrice,

          // Quantity and cart info
          product_quantity: 1,
          cart_id: app.$vsf.$magento.config.state.getCartId(),

          // Product variation details
          item_color_name: selectedOption.value.color?.label || "",
          item_size: selectedOption.value.size?.label || "",

          // Context information
          action_placement: "Product Detail Page",

          // URLs
          item_url: `${origin}/${product.url_key || ""}`,
          item_image_url: product.image?.url || "",

          // Stock information
          item_in_stock: stockData?.qty || 0,

          // Search context if applicable
          is_search_result: infoFromSearch?.is_search_result || false,
          seach_result_query: infoFromSearch?.seach_result_query || "",

          // List/category information
          list_id:
            infoFromSearch?.list_id ||
            (lastCategory ? decodeUid(lastCategory.uid) : ""),
          list_name:
            infoFromSearch?.list_name ||
            (lastCategory ? lastCategory.name : ""),
          position: infoFromSearch?.position || -1,

          // Recommendation context (default values)
          suggest_product: false,
          suggest_product_title: "",
          block_position: "",
          suggest_product_number: "",
          from_location: "",
          from_product_id: "",
        };

        // Update recommendation-related fields if product was viewed from a recommendation
        if (fromToData) {
          trackingData.from_location = fromToData.from_location || "";
          trackingData.from_product_id = fromToData.from_product_id || "";
          trackingData.suggest_product = fromToData.suggest_product || false;
          trackingData.suggest_product_title =
            fromToData.suggest_product_title || "";
          trackingData.block_position = fromToData.block_position || "";
          trackingData.suggest_product_number =
            fromToData.suggest_product_number || "";
        }

        // Send event to CleverTap following the documented pattern
        const win = window as any;
        if (typeof win !== "undefined") {
          // CleverTap tracking
          if (win.clevertap) {
            clevertapStore.productAddToCartCleverTap({
              clevertap: win.clevertap,
              data: trackingData,
            });
          }
          console.log("Tracking data for add to cart:", props.product);
          // Create common product data for tracking
          const productData = {
            parentSku: props.product.sku.includes("-")
              ? detailSku.substring(0, detailSku.lastIndexOf("-"))
              : props.product.sku,
            sku: detailSku,
            name: props.product.name,
            original_price: regularPrice || 0,
            final_price: finalPrice || 0,
            description: product.description_map || "",
            url_path: product.url_key || "",
            image:
              product.media_gallery && product.media_gallery.length > 0
                ? product.media_gallery.map((img) => ({ image: img.image }))
                : product.image_map
                ? [
                    {
                      image:
                        process.env.IMG_BASE_URL +
                        "/catalog/product" +
                        product.image_map,
                    },
                  ]
                : product.image
                ? [{ image: product.image }]
                : [],
          };

          const ecommercePixelStore = useEcommercePixel();

          // Facebook Pixel tracking
          if (win.fbq) {
            ecommercePixelStore.addToCartFbq({
              analytics: win.fbq,
              product: productData,
              userData: isAuthenticated.value && user.value ? user.value : null,
            });
          }

          // Google Tag Manager tracking
          if (win.dataLayer) {
            ecommercePixelStore.addToCartGtag({
              dataLayer: win.dataLayer,
              window_dataLayer: win.dataLayer,
              product: productData,
            });

            // Google Analytics 4 tracking
            ecommercePixelStore.addToCartGa4({
              dataLayer: win.dataLayer,
              window_dataLayer: win.dataLayer,
              product: productData,
            });
          }

          // TikTok Pixel tracking
          if (win.ttq) {
            ecommercePixelStore.addToCartTiktok({
              ttq: win.ttq,
              product: productData,
            });
          }
        }
      } catch (error) {
        console.error("Error tracking add to cart event:", error);
      }
    };
    const handleAddAlert = () => {
      if (!selectedOption.value.color) {
        showColorAlert.value = true;
      }
      if (!selectedOption.value.size) {
        showSizeAlert.value = true;
        setTimeout(() => {
          const alertElement = document.querySelector(".product__option-alert");
          if (
            alertElement &&
            alertElement.textContent?.includes("Vui lòng chọn kích cỡ")
          ) {
            alertElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      }
    };
    const availableOptions = computed(() => {
      return getConfigurableOptionsByProduct(configurableOptions.value);
    });
    const variants = computed(() => {
      return props.product?.configurable_children ?? [];
    });
    const skuList = computed(() => {
      return variants.value.map((variant) => variant?.sku);
    });
    const parentSku = computed((): string => {
      if (props.product && props.product.sku) {
        const skuArr = props.product.sku.split("-");
        return skuArr.length > 0 ? skuArr[0] : props.product.sku;
      }
      return "";
    });
    const isModalCheckStock = ref(false);
    const isModalSizeGuideOpen = ref(false);
    const checkCart = ref(false);
    const dataCheckOptionInstock = ref([]);
    const totalCount = ref(0);
    const selectedOption = ref({
      size: null,
      color: null,
    });
    const updateSelectedOption = (attributeOption: any) => {
      if (attributeOption.type === "color") {
        selectedOption.value.color = attributeOption;
        updateProductConfiguration(attributeUidColor, attributeOption?.uid);
      } else if (attributeOption.type === "size") {
        selectedOption.value.size = attributeOption;
        updateProductConfiguration(attributeUidSize, attributeOption?.uid);
      }
      emit("updateSelectedOption", selectedOption);
    };
    const checkStockBySkus = async () => {
      // if (props.product.type_id === "configurable") {
      const paramSkus = skuList.value
        .map((sku) => encodeURIComponent(sku))
        .join(",");
      dataCheckOptionInstock.value = await getProductListStock(paramSkus);
      checkoption();
      // } else {
      // Xử lý sản phẩm simple
      // }
    };
    const findMatchingVariant = (selectedOption: SelectedOption) => {
      const selectedColor = selectedOption.color;
      const selectedSize = selectedOption.size;
      const configurableOpt = props.product?.configurable_options_map || [];
      const hasColorAttribute = configurableOpt.some(
        (opt) => opt.attribute_code === "color"
      );
      const hasSizeAttribute = configurableOpt.some(
        (opt) => opt.attribute_code === "size"
      );
      return (
        variants.value?.find((variant) => {
          const colorMatch =
            !hasColorAttribute ||
            !selectedColor ||
            variant.color?.toString() ===
              selectedColor?.value_index?.toString();
          const sizeMatch =
            !hasSizeAttribute ||
            !selectedSize ||
            variant.size?.toString() === selectedSize?.value_index?.toString();
          if (hasColorAttribute && hasSizeAttribute) {
            return colorMatch && sizeMatch;
          }
          if (hasColorAttribute && !hasSizeAttribute) {
            return colorMatch;
          }
          if (!hasColorAttribute && hasSizeAttribute) {
            return colorMatch;
          }
        }) || null
      );
    };
    const findMatchingSku = (selectedOption: SelectedOption) => {
      return findMatchingVariant(selectedOption)?.sku || null;
    };
    const productShowPrice = computed(() => {
      return findMatchingVariant(selectedOption.value) || props.product;
    });
    const checkoption = () => {
      if (props.product?.is_pre_order) {
        checkCart.value = true;
        return;
      }
      if (
        !dataCheckOptionInstock.value ||
        dataCheckOptionInstock.value.length < 1
      ) {
        checkCart.value = false;
        return;
      }
      let sku;
      const { color, size } = selectedOption.value;
      if (!color && !size) {
        sku = parentSku.value;
      } else if (color && !size) {
        sku = `${parentSku.value}-${color.label}`;
      } else if (!color && size) {
        sku = `${parentSku.value}-00000-${size.label}`;
      } // Todo: Tối ưu đoạn này
      else if (color && size) {
        sku = findMatchingSku(selectedOption.value);
      }
      if (sku) {
        checkSkuInStock(sku);
      }
    };
    const checkSkuInStock = (sku: string) => {
      if (dataCheckOptionInstock.value[sku]) {
        checkCart.value = dataCheckOptionInstock.value[sku]?.is_in_stock;
        totalCount.value = dataCheckOptionInstock.value[sku]?.qty;
      } else {
        totalCount.value = 0;
        for (const key in dataCheckOptionInstock.value) {
          if (dataCheckOptionInstock.value[key].sku.includes(sku)) {
            totalCount.value =
              totalCount.value + dataCheckOptionInstock.value[key]?.qty;
          }
        }
        if (totalCount.value > 0) {
          checkCart.value = true;
        } else {
          checkCart.value = false;
        }
      }
    };
    const sizeGuideId = computed(() => {
      return props.product?.sizeguide.toString() || "";
    });
    const checkLocalStorageViewed = () => {
      if (props.product && props.product.sku) {
        const storageKey = "vsf-product-viewed";
        if (typeof localStorage !== "undefined") {
          if (localStorage.getItem(storageKey)) {
            let viewedProducts = JSON.parse(localStorage.getItem(storageKey));
            let index = viewedProducts?.findIndex(
              (e: string) => e === parentSku.value
            );
            if (index >= 0) {
              viewedProducts.splice(index, 1);
            }
            viewedProducts.push(parentSku.value);
            localStorage.setItem(storageKey, JSON.stringify(viewedProducts));
            listProductViewed.value = viewedProducts;
          } else {
            let data = [parentSku.value];
            localStorage.setItem(storageKey, JSON.stringify(data));
            listProductViewed.value = data;
          }
        }
      }
    };
    // Get list image từ product.variant.product.media_gallery
    const currentProductImages = computed(() => {
      let mediaGallery = [];
      const configurableOpt = props.product?.configurable_options_map || [];
      const hasColorAttribute = configurableOpt.some(
        (opt) => opt.attribute_code === "color"
      );
      const hasSizeAttribute = configurableOpt.some(
        (opt) => opt.attribute_code === "size"
      );
      if (selectedOption.value.color) {
        const colorId = selectedOption.value.color?.value_index;
        const variantWithSelectedColor = variants.value?.find((variant) => {
          return (
            hasColorAttribute &&
            variant.color?.toString() === colorId?.toString()
          );
        });
        // Lấy size của variant có màu đã chọn
        const sizeId = variantWithSelectedColor?.size;
        // Chỉ chọn màu sắc
        if (!mediaGallery.length) {
          for (const variant of variants.value) {
            if (
              variant.color?.toString() === colorId?.toString() &&
              variant.size?.toString() === sizeId?.toString() &&
              variant?.media_gallery?.length > 0
            ) {
              mediaGallery = [...mediaGallery, ...variant.media_gallery];
            }
          }
        }
      }
      // Nếu không có ảnh nào thì lấy media_gallery mặc định của product
      if (!mediaGallery.length) {
        return (mediaGallery =
          props.product?.media_gallery?.map((item) => ({
            url: item.image || "",
            label: item.lab || "",
          })) || []);
      }
      // Sắp xếp để đưa ảnh có image_types chứa thumbnail lên đầu
      const sortedMediaGallery = [...mediaGallery].sort((a, b) => {
        const aThumbnail = a.types?.includes("thumbnail") || false;
        const bThumbnail = b.types?.includes("thumbnail") || false;
        if (aThumbnail && !bThumbnail) return -1;
        if (!aThumbnail && bThumbnail) return 1;
        return 0;
      });
      // Đổi type thành { url: string, label: string }[]
      return sortedMediaGallery.map((item) => ({
        url: item.image || "",
        label: item.lab || "",
      }));
    });
    const mobileActionsRef = ref(null);
    const isMobileActionsFixed = ref(false);
    const mobileActionsOffsetTop = ref(0);
    // Function to handle scroll events
    const handleScroll = () => {
      if (typeof window !== "undefined") {
        const scrollY = window.scrollY || window.pageYOffset;
        isMobileActionsFixed.value =
          scrollY > mobileActionsOffsetTop.value - 200;
      }
    };
    const { send: sendNotification } = useUiNotification();
    const clevertapStore = useClevertapStore(); // Add CleverTap store
    const shareProduct = () => {
      if (navigator.share) {
        const shareData = {
          title: props.product?.name || "",
          text: props.product?.name || "",
          url: window.location.href,
        };
        navigator
          .share(shareData)
          .then(() => {
            console.log("Shared successfully");
          })
          .catch((error) => console.log("Error sharing:", error));
      } else {
        // Fallback for browsers that don't support the Web Share API
        // Create temporary input to copy the URL
        const tempInput = document.createElement("input");
        tempInput.value = window.location.href;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);
        sendNotification({
          id: Symbol("share-product-detail"),
          message: "Đã sao chép đường dẫn sản phẩm vào clipboard",
          type: "success",
          icon: "check",
          persist: false,
        });
      }
    };
    const provideProductData = (callback) => {
      // Provide the full product object to the child component
      if (typeof callback === "function") {
        callback(props.product);
      }
    };
    const productPrice = computed(() => {
      return getRegularPrice(props.product);
    });
    const productSpecialPrice = computed(() => {
      return getFinalPrice(props.product);
    });
    useFetch(async () => {
      const queryRoute = route.value?.query;
      if (queryRoute?.color || queryRoute?.size) {
        if (queryRoute.color) {
          // chọn màu, size từ url
          selectedOption.value.color = availableOptions.value.color?.find(
            (color) =>
              color.uid === queryRoute.color || color.label === queryRoute.color // Todo: Chưa control được queryRoute
          );
        }
        if (queryRoute.size) {
          selectedOption.value.size = availableOptions.value.size?.find(
            (size) =>
              size.uid === queryRoute.size || size.label === queryRoute.size
          );
        }
      } else if (
        !selectedOption.value.color &&
        availableOptions.value?.color?.length > 0
      ) {
        selectedOption.value.color = availableOptions.value.color[0]; // Load trang chọn màu từ card => nếu không chọn thì mặc định chọn màu đầu tiên
      }

      if (selectedOption.value.color) {
        // đẩy query lên url
        updateProductConfiguration(
          attributeUidColor,
          selectedOption.value.color?.uid
        );
      }
      if (selectedOption.value.size) {
        updateProductConfiguration(
          attributeUidSize,
          selectedOption.value.size?.uid
        );
      }
      await checkStockBySkus();

      // Add tracking of viewed products
      checkLocalStorageViewed();
      // Get the initial offsetTop of the mobile actions element
      if (typeof window !== "undefined") {
        setTimeout(() => {
          if (mobileActionsRef.value) {
            // Get the position relative to the document
            const rect = mobileActionsRef.value.getBoundingClientRect();
            const scrollY = window.scrollY || window.pageYOffset;
            mobileActionsOffsetTop.value = rect.top + scrollY;
            // Add scroll event listener
            window.addEventListener("scroll", handleScroll, { passive: true });
            // Initial check
            handleScroll();
          }
        }, 100);
      }
    });
    const copySku = () => {
      if (props.product && props.product.sku) {
        copyCode(props.product.sku);
        sendNotification({
          id: Symbol("copy-sku"),
          message: "Đã sao chép mã sản phẩm " + props.product.sku,
          type: "success",
          icon: "check",
          persist: false,
        });
      }
    };
    onBeforeUnmount(() => {
      // Clean up the scroll event listener
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", handleScroll);
      }
    });
    watch(
      () => selectedOption.value.color,
      (newVal, oldVal) => {
        if (!oldVal || newVal.uid === oldVal.uid) {
          return;
        }
        selectedOption.value.size = null;
        delete productConfiguration.value[attributeUidSize];
        updateConfiguration();
      }
    );
    watch(
      () => selectedOption.value,
      () => {
        checkoption();
      },
      { deep: true }
    );
    return {
      addItem,
      canAddToCart,
      loadingStock,
      configurableOptions,
      updateProductConfiguration,
      isAuthenticated,
      isCartLoading,
      productConfiguration,
      getProductName,
      getProductSwatchData,
      productShortDescription,
      qty,
      totalReviews,
      averageRating,
      addToCartError,
      handleAddToCart,
      handleAddAlert,
      selectedOption,
      nl2br,
      isModalSizeGuideOpen,
      isModalCheckStock,
      updateSelectedOption,
      checkCart,
      dataCheckOptionInstock,
      parentSku,
      availableOptions,
      sizeGuideId,
      listProductViewed,
      currentProductImages,
      showColorAlert,
      showSizeAlert,
      // handleWishlistClick,
      // addItemToWishlist: addOrRemoveItem,
      // wishlistState,
      mobileActionsRef,
      isMobileActionsFixed,
      shareProduct,
      copySku,
      provideProductData,
      trackAddToCart,
      variants,
      productPrice,
      productSpecialPrice,
      productShowPrice,
    };
  },
});
